import React, { useMemo } from "react";

export type GenerativeRecentProduct = "image-generator" | "collection-build";

export type GenerativeRecentItem = {
  key: string;
  kind: "image" | "collection";
  id: number;
  sessionId?: number;
  title: string;
  status: string;
  createdAt: string;
};

type Props = {
  /** Required so CB and IG never share an ambiguous Recents list. */
  product: GenerativeRecentProduct;
  items: GenerativeRecentItem[];
  activeKey: string | null;
  collapsed: boolean;
  emptyCopy: string;
  onToggle: () => void;
  onSelect: (item: GenerativeRecentItem) => void;
};

const kindForProduct = (
  product: GenerativeRecentProduct
): GenerativeRecentItem["kind"] =>
  product === "collection-build" ? "collection" : "image";

const statusLabel = (item: GenerativeRecentItem) => {
  if (item.status === "generating") {
    return "Generating…";
  }
  if (item.status === "failed") {
    return "Failed";
  }
  const created = new Date(item.createdAt);
  if (Number.isNaN(created.getTime())) {
    return "Ready";
  }
  return created.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * Presentational Recents list shared by IG and CB wrappers.
 * Product filtering is mandatory — callers must not mix kinds.
 */
const GenerativeRecentsList: React.FC<Props> = ({
  product,
  items,
  activeKey,
  collapsed,
  emptyCopy,
  onToggle,
  onSelect,
}) => {
  const expectedKind = kindForProduct(product);
  const safeItems = useMemo(
    () =>
      items.filter(
        item =>
          item.kind === expectedKind &&
          Number.isInteger(item.id) &&
          item.id > 0 &&
          item.key.startsWith(`${expectedKind}-`)
      ),
    [expectedKind, items]
  );

  return (
    <div className="mt-2 px-1">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left text-secondary hover:bg-gray"
        aria-expanded={!collapsed}
      >
        <span className="text-sm font-medium">Recents</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          className={`shrink-0 transition-transform ${
            collapsed ? "-rotate-90" : ""
          }`}
          aria-hidden="true"
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {!collapsed && (
        <div className="mt-1 max-h-[40vh] overflow-y-auto">
          {safeItems.length === 0 ? (
            <p className="px-2 py-2 text-xs text-tertiary">{emptyCopy}</p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {safeItems.map(item => {
                const active = item.key === activeKey;
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => onSelect(item)}
                      className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
                        active
                          ? "bg-[#F3EFEA] text-black"
                          : "text-secondary hover:bg-gray"
                      }`}
                    >
                      <span className="block truncate text-xs font-medium">
                        {item.title}
                      </span>
                      <span
                        className={`mt-0.5 block truncate text-[11px] ${
                          item.status === "failed"
                            ? "text-red-600"
                            : "text-secondary"
                        }`}
                      >
                        {statusLabel(item)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default GenerativeRecentsList;
